import React, { useState, useEffect } from "react";
import {
  database,
  ref,
  push,
  onValue,
  remove,
  update,
} from "./firebase";
import { useNavigate } from "react-router-dom";
import "./BillingSystem.css";
import Navigation from "./Navigation";

import {
  products,
  formatDate,
  showNotification,
} from "./salesmanHelpers";

export default function SalesmanBillingSystem({ phone }) {
  const navigate = useNavigate();

  const [quantities, setQuantities] = useState(Array(products.length).fill(0));
  const [schemes, setSchemes] = useState(Array(products.length).fill(0));
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [totalAmount, setTotalAmount] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("");
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  // Route/Address state
  const [newRoute, setNewRoute] = useState("");
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [newCustomerRoute, setNewCustomerRoute] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // Load salesman profile, invoices, routes and customers
  useEffect(() => {
    if (!phone) return;

    // Load salesman name
    onValue(ref(database, `users/${phone}/name`), (snap) => {
      if (snap.exists()) setUserName(snap.val());
    });

    // Load salesman invoices
    onValue(ref(database, `salesmanInvoices/${phone}`), (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([id, inv]) => ({
          id,
          ...inv,
        }));
        setInvoices(list.reverse());
      }
    });

    // Load saved routes
    onValue(ref(database, `routes/${phone}`), (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        setRoutes(Object.values(data));
      }
    });

    // Load saved customers
    onValue(ref(database, `salesmanCustomers/${phone}`), (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.values(data);
        setCustomers(list);
      }
    });
  }, [phone]);

  // Add new route
  const addRoute = async () => {
    if (!newRoute.trim()) return showNotification("Enter a route/address");
    await push(ref(database, `routes/${phone}`), newRoute.trim());
    showNotification("Route added successfully!");
    setNewRoute("");
  };

  // Add new customer
  const addCustomer = async () => {
    if (
      !newCustomerName.trim() ||
      !newCustomerPhone.match(/^\d{10}$/) ||
      !newCustomerRoute.trim()
    ) {
      return showNotification("Enter valid name, phone & route");
    }
    await push(ref(database, `salesmanCustomers/${phone}`), {
      name: newCustomerName.trim(),
      phone: newCustomerPhone.trim(),
      route: newCustomerRoute.trim(),
    });
    showNotification("Customer added successfully!");
    setNewCustomerName("");
    setNewCustomerPhone("");
    setNewCustomerRoute("");
  };

  // Handle typing in customer name (auto-suggestions)
  const handleCustomerNameChange = (val) => {
    setCustomerName(val);
    if (val.length > 0) {
      const matches = customers.filter((c) =>
        c.name.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  };

  // Select customer from suggestions
  const selectCustomer = (c) => {
    setCustomerName(c.name);
    setCustomerPhone(c.phone);
    setSelectedRoute(c.route);
    setSuggestions([]);
  };

  // Generate or update invoice
  const handleGenerateInvoice = async () => {
    if (!selectedRoute) return showNotification("Select a route first!");
    if (!customerPhone.match(/^\d{10}$/))
      return showNotification("Invalid phone number");
    if (!quantities.some((q) => q > 0) && !schemes.some((s) => s > 0))
      return showNotification("Enter at least one quantity");

    const items = products
      .map((p, i) => ({
        product: p,
        quantity: quantities[i],
        scheme: schemes[i],
      }))
      .filter((i) => i.quantity > 0 || i.scheme > 0);

    if (editingInvoiceId) {
      await update(
        ref(database, `salesmanInvoices/${phone}/${editingInvoiceId}`),
        {
          customerName,
          customerPhone,
          createdBy: userName,
          paymentMethod,
          totalAmount: totalAmount || "",
          route: selectedRoute,
          date: formatDate(new Date()),
          items,
        }
      );
      showNotification("Invoice updated successfully!");
    } else {
      await push(ref(database, `salesmanInvoices/${phone}`), {
        customerName,
        customerPhone,
        createdBy: userName,
        paymentMethod,
        totalAmount: totalAmount || "",
        route: selectedRoute,
        date: formatDate(new Date()),
        items,
      });
      showNotification("Invoice created successfully!");
    }

    // Reset form
    setQuantities(Array(products.length).fill(0));
    setSchemes(Array(products.length).fill(0));
    setCustomerName("");
    setCustomerPhone("");
    setTotalAmount("");
    setPaymentMethod("Cash");
    setEditingInvoiceId(null);
    setSelectedRoute("");
  };

  // Edit invoice
  const editInvoice = (invoice) => {
    setEditingInvoiceId(invoice.id);
    setCustomerName(invoice.customerName || "");
    setCustomerPhone(invoice.customerPhone || "");
    setPaymentMethod(invoice.paymentMethod || "Cash");
    setTotalAmount(invoice.totalAmount || "");
    setSelectedRoute(invoice.route || "");
    const newQuantities = Array(products.length).fill(0);
    const newSchemes = Array(products.length).fill(0);
    invoice.items.forEach((item) => {
      const index = products.indexOf(item.product);
      if (index !== -1) {
        newQuantities[index] = item.quantity || 0;
        newSchemes[index] = item.scheme || 0;
      }
    });
    setQuantities(newQuantities);
    setSchemes(newSchemes);
  };

  // Delete invoice
  const deleteInvoice = async (id) => {
    if (window.confirm("Delete this invoice?")) {
      await remove(ref(database, `salesmanInvoices/${phone}/${id}`));
      showNotification("Invoice deleted");
    }
  };

  const filteredInvoices = invoices.filter(
    (inv) =>
      (inv.customerName?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) || inv.date?.includes(searchTerm)
  );

  return (
    <div className="container">
      <h2>Salesman Billing System</h2>
      <Navigation role="salesman" />

      <p>
        Logged in as <strong>{userName}</strong>
      </p>

      {/* Add Route Section */}
      <div className="section">
        <h4>Add Route/Address</h4>
        <input
          placeholder="Enter Route or Address"
          value={newRoute}
          onChange={(e) => setNewRoute(e.target.value)}
        />
        <button onClick={addRoute}>Add Route</button>
      </div>

      {/* Add Customer Section */}
      <div className="section">
        <h4>Add Customer</h4>
        <input
          placeholder="Customer Name"
          value={newCustomerName}
          onChange={(e) => setNewCustomerName(e.target.value)}
        />
        <input
          placeholder="Phone Number"
          value={newCustomerPhone}
          onChange={(e) => setNewCustomerPhone(e.target.value)}
        />

        {/* Route Dropdown instead of manual input */}
        <select
          value={newCustomerRoute}
          onChange={(e) => setNewCustomerRoute(e.target.value)}
        >
          <option value="">-- Select Route --</option>
          {routes.map((r, i) => (
            <option key={i} value={r}>
              {r}
            </option>
          ))}
        </select>

        <button onClick={addCustomer}>Add Customer</button>
      </div>

      {/* Create Invoice */}
      <div className="section">
        <h4>{editingInvoiceId ? "Update Invoice" : "Create Invoice"}</h4>

        {/* Product Table Header */}
        <div className="product-row header">
          <div className="col-product">Product</div>
          <div className="col-qty">Quantity</div>
          <div className="col-scheme">Scheme</div>
        </div>

        {/* Product Rows */}
        {products.map((p, i) => (
          <div key={i} className="product-row">
            <div className="col-product">{p}</div>
            <input
              type="number"
              className="col-qty"
              value={quantities[i]}
              onChange={(e) => {
                const arr = [...quantities];
                arr[i] = Number(e.target.value);
                setQuantities(arr);
              }}
            />
            <input
              type="number"
              className="col-scheme"
              value={schemes[i]}
              onChange={(e) => {
                const arr = [...schemes];
                arr[i] = Number(e.target.value);
                setSchemes(arr);
              }}
            />
          </div>
        ))}

        {/* Customer Auto-suggest Input with Dropdown */}
<input
  placeholder="Customer Name"
  value={customerName}
  onChange={(e) => handleCustomerNameChange(e.target.value)}
/>

{suggestions.length > 0 && (
  <select
    size={Math.min(suggestions.length, 5)} // show max 5
    onChange={(e) => {
      const selected = customers.find(
        (c) => c.phone === e.target.value
      );
      if (selected) selectCustomer(selected);
    }}
  >
    <option value="">-- Select Customer --</option>
    {suggestions.map((c, i) => (
      <option key={i} value={c.phone}>
        {c.name} ({c.phone}) - {c.route}
      </option>
    ))}
  </select>
)}

        <input
          placeholder="Phone Number"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value)}
        />

        {/* Route dropdown */}
        <select
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
        >
          <option value="">-- Select Route --</option>
          {routes.map((r, i) => (
            <option key={i} value={r}>
              {r}
            </option>
          ))}
        </select>

        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <option>Cash</option>
          <option>Google Pay</option>
          <option>On credit</option>
        </select>

        <input
          placeholder="Total Amount (optional)"
          value={totalAmount}
          onChange={(e) => setTotalAmount(e.target.value)}
        />

        <button onClick={handleGenerateInvoice}>
          {editingInvoiceId ? "Update" : "Create Invoice"}
        </button>
      </div>

      {/* Invoice History */}
      <div className="section">
        <h4>Invoice History</h4>
        <input
          placeholder="Search by name or date"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {filteredInvoices.length === 0 ? (
          <p>No invoices found.</p>
        ) : (
          <div className="token-history">
            {filteredInvoices.map((inv) => (
              <div key={inv.id} className="token-card">
                <p>
                  <strong className="token-title">
                    {inv.customerName} ({inv.customerPhone})
                  </strong>
                </p>
                <p className="token-date">{inv.date}</p>
                <p>
                  <strong>Route:</strong> {inv.route || "N/A"}
                </p>
                <p>
                  <strong>Bill Created By:</strong> {inv.createdBy || userName}
                </p>
                <p>
                  <strong>Payment:</strong> {inv.paymentMethod || "Cash"}
                </p>
                <p className="token-total">
                  <strong>Total:</strong> ₹{inv.totalAmount || 0}
                </p>
                <ul className="token-items">
                  {inv.items &&
                    inv.items.map((item, idx) => (
                      <li key={idx}>
                        {item.quantity} x {item.product}
                        {item.scheme > 0 ? ` (Scheme: ${item.scheme})` : ""}
                      </li>
                    ))}
                </ul>
                <div className="token-actions">
                  <button
                    className="btn edit-btn"
                    onClick={() => editInvoice(inv)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn delete-btn"
                    onClick={() => deleteInvoice(inv.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
